import React, { useState, useEffect, useRef } from 'react';
import API from '../../services/api';
import AddCustomerModal from './AddCustomerModal';

const emptyBike = () => ({ bike_master_id: null, color_id: '', chassis_number: '', engine_number: '', engine_error: '', chassis_error: '' });

const AddPurchaseForm = ({ onComplete }) => {
    const [purchaseType, setPurchaseType] = useState('Supplier');
    const [supplierResults, setSupplierResults] = useState([]);
    const [paymentAccounts, setPaymentAccounts] = useState([]);
    const [colors, setColors] = useState([]);
    const [makesList, setMakesList] = useState([]);
    const [modelsList, setModelsList] = useState([]);
    const [powersList, setPowersList] = useState([]);

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [supplierSelectedIndex, setSupplierSelectedIndex] = useState(-1);

    const [customerSearch, setCustomerSearch] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [showCustomerModal, setShowCustomerModal] = useState(false);

    // Search state for Market & Buy_Back
    const [searchCode, setSearchCode] = useState('');
    const [marketSearchResults, setMarketSearchResults] = useState([]);
    const [isMarketDropdownOpen, setIsMarketDropdownOpen] = useState(false);
    const [marketSelectedIndex, setMarketSelectedIndex] = useState(-1);

    const [foundBike, setFoundBike] = useState(null); 
    const [showManualForm, setShowManualForm] = useState(false); 

    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [remarks, setRemarks] = useState('');
    const [loading, setLoading] = useState(false);

    const [lotDetails, setLotDetails] = useState({ model_name: '', version: '', cc: '', unit_price: '' });
    const [bikes, setBikes] = useState([emptyBike()]);
    const [payments, setPayments] = useState([{ method: 'Cash', account_id: '', amount: '' }]);

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        try {
            const [resAcc, resColors, resMakes, resModels, resPowers] = await Promise.all([
                API.get('/v1/purchase/payment-accounts'),
                API.get('/v1/colors'),
                API.get('/v1/specs/makes').catch(() => ({ data: { data: [] } })),
                API.get('/v1/specs/models').catch(() => ({ data: { data: [] } })),
                API.get('/v1/specs/powers').catch(() => ({ data: { data: [] } }))
            ]);
            setPaymentAccounts(resAcc.data.data || resAcc.data || []);
            setColors(resColors.data.data || resColors.data || []);
            setMakesList(resMakes.data.data || resMakes.data || []);
            setModelsList(resModels.data.data || resModels.data || []);
            setPowersList(resPowers.data.data || resPowers.data || []);
        } catch (err) {
            console.error('Initialization Error:', err);
        }
    };

    // Debounced Supplier Search
    useEffect(() => {
        const term = searchTerm.trim();
        if (!term) { setSupplierResults([]); setSupplierSelectedIndex(-1); return; }
        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                const res = await API.get('/v1/suppliers/search', { params: { query: term } });
                if (!cancelled) {
                    setSupplierResults(res.data.data || []);
                    setSupplierSelectedIndex(-1);
                }
            } catch (err) { if (!cancelled) setSupplierResults([]); }
        }, 300);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [searchTerm]);

    const handleTabSwitch = (t) => {
        setPurchaseType(t);
        setSelectedCustomer(null);
        setSelectedSupplier(null);
        setSearchTerm('');
        setCustomerSearch('');
        setSearchCode('');
        setFoundBike(null);
        setShowManualForm(false);
        setBikes([emptyBike()]);
        setLotDetails({ model_name: '', version: '', cc: '', unit_price: '' });
    };

    const handleBikeSearchInput = async (val) => {
        setSearchCode(val);
        if (!val.trim()) {
            setMarketSearchResults([]);
            setIsMarketDropdownOpen(false);
            setMarketSelectedIndex(-1);
            return;
        }

        try {
            const res = await API.get(`/v1/purchase/search-sold-bikes?query=${encodeURIComponent(val.trim())}`);
            if (res.data.success) {
                setMarketSearchResults(res.data.data || []);
                setIsMarketDropdownOpen(true);
                setMarketSelectedIndex(-1);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const selectBikeItem = async (bike) => {
        if (purchaseType === 'Buy_Back') {
            if (!selectedCustomer) return alert('Please select a customer first.');
            try {
                const res = await API.post('/v1/purchase/verify-buyback', { query: bike.engine_number });
                if (res.data.valid && res.data.bikeData) {
                    const b = res.data.bikeData;
                    setFoundBike(b);
                    setLotDetails({ model_name: b.model_name, version: b.version, cc: b.cc, unit_price: '' });
                    setBikes([{ bike_master_id: b.bike_master_id, color_id: b.color, chassis_number: b.chassis_number, engine_number: b.engine_number }]);
                    setShowManualForm(false);
                } else {
                    alert(res.data.message || 'Verification failed.');
                }
            } catch (err) {
                alert(err.response?.data?.error || 'Verification request failed.');
            }
        } else {
            setFoundBike(bike);
            setLotDetails({ model_name: bike.model_name, version: bike.version, cc: bike.cc, unit_price: '' });
            setBikes([{ bike_master_id: bike.bike_master_id, color_id: bike.color, chassis_number: bike.chassis_number, engine_number: bike.engine_number }]);
            setShowManualForm(false);
        }
        setIsMarketDropdownOpen(false);
        setSearchCode(`${bike.model_name} - Eng: ${bike.engine_number}`);
    };

    const totalAmount = Math.round((parseFloat(lotDetails.unit_price) || 0) * bikes.length);

    const updateBike = (index, changes) => {
        setBikes(prev => prev.map((b, i) => (i === index ? { ...b, ...changes } : b)));
    };

    const handleBikeDetailChange = (index, field, value) => {
        const changes = { [field]: value };
        if (field === 'engine_number') changes.engine_error = '';
        if (field === 'chassis_number') changes.chassis_error = '';
        updateBike(index, changes);
    };

    const checkDuplicate = async (index, field) => {
        const value = (bikes[index][field] || '').trim();
        const errorKey = field === 'engine_number' ? 'engine_error' : 'chassis_error';
        const label = field === 'engine_number' ? 'Engine number' : 'Chassis number';
        if (value.length < 3) return;

        try {
            const res = await API.post('/v1/purchase/check-duplicate-stock', { [field]: value });
            if (res.data && res.data.exists) {
                updateBike(index, { [errorKey]: `${label} already exists in available inventory.` });
            }
        } catch (err) { console.error(err); }
    };

    const addPaymentRow = () => setPayments([...payments, { method: 'Cash', account_id: '', amount: '' }]);
    const removePaymentRow = (index) => setPayments(payments.filter((_, i) => i !== index));

    const updatePayment = (index, changes) => {
        setPayments(prev => prev.map((p, i) => (i === index ? { ...p, ...changes } : p)));
    };

    const submitForm = async () => {
        if (purchaseType === 'Supplier' && !selectedSupplier) return alert('Please select a supplier.');
        if ((purchaseType === 'Market' || purchaseType === 'Buy_Back') && !selectedCustomer) return alert('Please select a customer.');

        if (!lotDetails.model_name || !lotDetails.version || !lotDetails.unit_price) {
            return alert('Make, Version, and Unit Price are required.');
        }

        for (let i = 0; i < bikes.length; i++) {
            const b = bikes[i];
            if (!b.color_id || !b.engine_number.trim() || !b.chassis_number.trim()) {
                return alert(`Bike #${i + 1}: Color, Engine number, and Chassis number are required.`);
            }
        }

        setLoading(true);
        try {
            const payload = {
                purchase_type: purchaseType,
                supplier_id: selectedSupplier?.id || null,
                customer_id: selectedCustomer?.id || null,
                invoice_number: invoiceNumber,
                remarks,
                model_name: lotDetails.model_name,
                version: lotDetails.version,
                cc: lotDetails.cc,
                unit_price: lotDetails.unit_price,
                payments,
                bikes: bikes.map(b => ({
                    bike_master_id: b.bike_master_id || null,
                    color_id: b.color_id,
                    engine_number: b.engine_number.trim(),
                    chassis_number: b.chassis_number.trim()
                }))
            };

            const res = await API.post('/v1/purchase/comprehensive-process', payload);
            if (res.data.success) {
                alert('Purchase transaction saved successfully.');
                if (onComplete) onComplete();
            }
        } catch (err) {
            alert(err.response?.data?.error || 'Server processing error encountered.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={containerStyle}>
            {/* HEADER TABS */}
            <div style={headerCard}>
                <div>
                    <h2 style={{ margin: 0, fontSize: '18px', color: '#0f172a', fontWeight: '700' }}>
                        {purchaseType === 'Supplier' ? 'New Purchase (Bulk Supplier)' : `Purchase (${purchaseType.replace('_', ' ')})`}
                    </h2>
                    <span style={{ fontSize: '11px', color: '#64748b' }}>ERP Purchase & Stock Module</span>
                </div>
                <div style={tabContainer}>
                    {['Supplier', 'Market', 'Buy_Back'].map(t => (
                        <button key={t} style={purchaseType === t ? activeTab : tabStyle} onClick={() => handleTabSwitch(t)}>
                            {t.replace('_', ' ')}
                        </button>
                    ))}
                </div>
            </div>

            {/* TOP META */}
            <div style={metaGrid}>
                <div style={metaFieldBox}>
                    <span style={metaLabel}>PV #</span>
                    <input style={disabledInput} value="Auto Generated" readOnly />
                </div>
                <div style={metaFieldBox}>
                    <span style={metaLabel}>DATE</span>
                    <input style={inputStyle} defaultValue={new Date().toISOString().split('T')[0]} />
                </div>
            </div>

            {/* PARTY & SEARCH BOX */}
            <div style={sectionBox}>
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
                    <span style={{ fontWeight: '600', fontSize: '12px', color: '#334155' }}>
                        {purchaseType === 'Supplier' ? 'Supplier' : 'Customer / Seller'}
                    </span>
                    {purchaseType === 'Supplier' ? (
                        !selectedSupplier ? (
                            <div style={{ position: 'relative' }}>
                                <input
                                    style={inputStyle}
                                    placeholder="Search supplier..."
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (supplierResults.length === 0) return;
                                        if (e.key === 'ArrowDown') {
                                            e.preventDefault();
                                            setSupplierSelectedIndex(prev => (prev < supplierResults.length - 1 ? prev + 1 : 0));
                                        } else if (e.key === 'ArrowUp') {
                                            e.preventDefault();
                                            setSupplierSelectedIndex(prev => (prev > 0 ? prev - 1 : supplierResults.length - 1));
                                        } else if (e.key === 'Enter' && supplierSelectedIndex >= 0) {
                                            e.preventDefault();
                                            setSelectedSupplier(supplierResults[supplierSelectedIndex]);
                                            setSearchTerm('');
                                            setSupplierResults([]);
                                            setSupplierSelectedIndex(-1);
                                        }
                                    }}
                                />
                                {searchTerm.trim() && supplierResults.length > 0 && (
                                    <div style={dropdownStyle}>
                                        {supplierResults.map((s, idx) => (
                                            <div 
                                                key={s.id} 
                                                style={{ ...itemStyle, background: idx === supplierSelectedIndex ? '#f1f5f9' : '#fff' }} 
                                                onClick={() => { setSelectedSupplier(s); setSearchTerm(''); setSupplierResults([]); }}
                                            >
                                                {s.supplier_name} — {s.phone}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div style={confirmBox}>
                                <div><b>{selectedSupplier.supplier_name}</b> ({selectedSupplier.phone})</div>
                                <button onClick={() => setSelectedSupplier(null)} style={changeBtn}>Change</button>
                            </div>
                        )
                    ) : (
                        !selectedCustomer ? (
                            <div style={{ position: 'relative', display: 'flex', gap: '5px' }}>
                                <input
                                    style={{ ...inputStyle, flex: 1 }}
                                    placeholder="Search Customer..."
                                    value={customerSearch}
                                    onChange={e => setCustomerSearch(e.target.value)}
                                />
                                <button onClick={() => setShowCustomerModal(true)} style={addBtnStyle}>+ Add</button>
                                {customerSearch && (
                                    <CustomerDropdownSearch 
                                        query={customerSearch} 
                                        onSelect={(cust) => { setSelectedCustomer(cust); setCustomerSearch(''); }} 
                                    />
                                )}
                            </div>
                        ) : (
                            <div style={confirmBox}>
                                <div><b>{selectedCustomer.full_name} ({selectedCustomer.phone})</b></div>
                                <button onClick={() => setSelectedCustomer(null)} style={changeBtn}>Change</button>
                            </div>
                        )
                    )}
                </div>

                {/* SEARCH BAR FOR MARKET AND BUY_BACK WITH KEYBOARD NAVIGATION */}
                {(purchaseType === 'Market' || purchaseType === 'Buy_Back') && (
                    <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: '120px 1fr', gap: '10px', alignItems: 'center', marginBottom: '10px', background: '#e2e8f0', padding: '10px', borderRadius: '6px' }}>
                        <span style={{ fontWeight: '700', fontSize: '12px', color: '#0f172a' }}>Search Bike</span>
                        <input
                            style={inputStyle}
                            placeholder="Type Engine # or Chassis # to search sold bikes..."
                            value={searchCode}
                            onChange={e => handleBikeSearchInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (!isMarketDropdownOpen || marketSearchResults.length === 0) return;
                                if (e.key === 'ArrowDown') {
                                    e.preventDefault();
                                    setMarketSelectedIndex(prev => (prev < marketSearchResults.length - 1 ? prev + 1 : 0));
                                } else if (e.key === 'ArrowUp') {
                                    e.preventDefault();
                                    setMarketSelectedIndex(prev => (prev > 0 ? prev - 1 : marketSearchResults.length - 1));
                                } else if (e.key === 'Enter' && marketSelectedIndex >= 0) {
                                    e.preventDefault();
                                    selectBikeItem(marketSearchResults[marketSelectedIndex]);
                                }
                            }}
                        />
                        {isMarketDropdownOpen && marketSearchResults.length > 0 && (
                            <div style={dropdownStyle}>
                                {marketSearchResults.map((bike, idx) => (
                                    <div 
                                        key={bike.inventory_stock_id} 
                                        style={{ ...itemStyle, background: idx === marketSelectedIndex ? '#f1f5f9' : '#fff' }} 
                                        onClick={() => selectBikeItem(bike)}
                                    >
                                        <div><b>{bike.model_name}</b> (Eng: {bike.engine_number} | Chassis: {bike.chassis_number})</div>
                                        <div style={{ fontSize: '10px', color: '#64748b' }}>Status: {bike.stock_status}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '10px', alignItems: 'center' }}>
                    <span style={{ fontWeight: '600', fontSize: '12px', color: '#334155' }}>D.O # (Lot #)</span>
                    <input
                        style={inputStyle}
                        placeholder="Lot / Invoice Number"
                        value={invoiceNumber}
                        onChange={e => setInvoiceNumber(e.target.value)}
                    />
                </div>
            </div>

            {/* FOUND BIKE DISPLAY CARD */}
            {(purchaseType === 'Market' || purchaseType === 'Buy_Back') && foundBike && (
                <div style={{ ...sectionBox, background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#166534', fontSize: '14px' }}>✅ Selected Motorcycle Details</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', fontSize: '13px', color: '#1e293b' }}>
                        <div><b>Make:</b> {foundBike.model_name}</div>
                        <div><b>Version / Model:</b> {foundBike.version}</div>
                        <div><b>CC:</b> {foundBike.cc || 'N/A'}</div>
                        <div><b>Color:</b> {foundBike.color}</div>
                        <div><b>Engine #:</b> {foundBike.engine_number}</div>
                        <div><b>Chassis #:</b> {foundBike.chassis_number}</div>
                    </div>
                    <div style={{ marginTop: '15px', display: 'grid', gridTemplateColumns: '120px 1fr', gap: '10px', alignItems: 'center' }}>
                        <label style={labelStyle}>Agreed Price (Rs.)</label>
                        <input
                            style={{ ...inputStyle, fontSize: '14px', fontWeight: 'bold' }}
                            type="number"
                            placeholder="Enter purchase price"
                            value={lotDetails.unit_price}
                            onChange={e => setLotDetails({ ...lotDetails, unit_price: e.target.value })}
                        />
                    </div>
                </div>
            )}

            {/* MANUAL FORM (Supplier OR Market when Bike NOT found) */}
            {(purchaseType === 'Supplier' || (purchaseType === 'Market' && showManualForm)) && (
                <div style={sectionBox}>
                    <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#334155' }}>Motorcycle Specifications</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '10px', marginBottom: '10px' }}>
                        <label style={labelStyle}>Make</label>
                        <select style={inputStyle} value={lotDetails.model_name} onChange={e => setLotDetails({ ...lotDetails, model_name: e.target.value })}>
                            <option value="">Select Make</option>
                            {makesList.map((m, idx) => <option key={idx} value={m.name || m}>{m.name || m}</option>)}
                        </select>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '10px', marginBottom: '10px' }}>
                        <label style={labelStyle}>Version (Model)</label>
                        <select style={inputStyle} value={lotDetails.version} onChange={e => setLotDetails({ ...lotDetails, version: e.target.value })}>
                            <option value="">Select Version</option>
                            {modelsList.map((v, idx) => <option key={idx} value={v.version || v.name || v}>{v.version || v.name || v}</option>)}
                        </select>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '10px', marginBottom: '10px' }}>
                        <label style={labelStyle}>CC (Power)</label>
                        <select style={inputStyle} value={lotDetails.cc} onChange={e => setLotDetails({ ...lotDetails, cc: e.target.value })}>
                            <option value="">Select CC</option>
                            {powersList.map((p, idx) => <option key={idx} value={p.cc || p.name || p}>{p.cc || p.name || p}</option>)}
                        </select>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr 1fr', gap: '10px', marginBottom: '15px' }}>
                        <label style={labelStyle}>Unit Price</label>
                        <input style={inputStyle} type="number" placeholder="Single unit price" value={lotDetails.unit_price} onChange={e => setLotDetails({ ...lotDetails, unit_price: e.target.value })} />
                        <div style={{ textAlign: 'right', fontWeight: 'bold' }}>Total: Rs. {isNaN(totalAmount) ? 0 : totalAmount}</div>
                    </div>

                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#f1f5f9', fontSize: '11px' }}>
                                    <th style={{ padding: '8px' }}>#</th>
                                    <th style={{ padding: '8px' }}>Color</th>
                                    <th style={{ padding: '8px' }}>Engine #</th>
                                    <th style={{ padding: '8px' }}>Chassis #</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bikes.map((b, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #f8fafc' }}>
                                        <td style={{ padding: '8px' }}>{i + 1}</td>
                                        <td style={{ padding: '6px' }}>
                                            <select style={inputStyle} value={b.color_id} onChange={e => handleBikeDetailChange(i, 'color_id', e.target.value)}>
                                                <option value="">Color</option>
                                                {colors.map(c => <option key={c.id || c.name} value={c.name}>{c.name}</option>)}
                                            </select>
                                        </td>
                                        <td style={{ padding: '6px' }}>
                                            <input style={b.engine_error ? errorInput : inputStyle} placeholder="Engine #" value={b.engine_number} onChange={e => handleBikeDetailChange(i, 'engine_number', e.target.value)} onBlur={() => checkDuplicate(i, 'engine_number')} />
                                        </td>
                                        <td style={{ padding: '6px' }}>
                                            <input style={b.chassis_error ? errorInput : inputStyle} placeholder="Chassis #" value={b.chassis_number} onChange={e => handleBikeDetailChange(i, 'chassis_number', e.target.value)} onBlur={() => checkDuplicate(i, 'chassis_number')} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* PAYMENT BREAKDOWN */}
            <div style={sectionBox}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4 style={{ margin: 0, fontSize: '13px', color: '#334155' }}>Payment Breakdown (Cash / Bank)</h4>
                    <button onClick={addPaymentRow} style={addBtnStyle}>+ Add Payment</button>
                </div>
                {payments.map((p, i) => (
                    <div key={i} style={paymentRow}>
                        <select style={inputStyle} value={p.method} onChange={e => updatePayment(i, { method: e.target.value, account_id: '' })}>
                            <option value="Cash">Cash</option>
                            <option value="Bank">Bank Transfer</option>
                        </select>
                        <select style={inputStyle} value={p.account_id} onChange={e => updatePayment(i, { account_id: e.target.value })}>
                            <option value="">Select Account ({p.method})</option>
                            {paymentAccounts.filter(acc => acc.account_type === p.method).map(acc => (
                                <option key={acc.id} value={acc.id}>{acc.title}</option>
                            ))}
                        </select>
                        <input style={inputStyle} type="number" placeholder="Amount" value={p.amount} onChange={e => updatePayment(i, { amount: e.target.value })} />
                        <button onClick={() => removePaymentRow(i)} style={deleteBtn}>✕</button>
                    </div>
                ))}
            </div>

            {/* REMARKS */}
            <div style={sectionBox}>
                <label style={miniLabel}>Remarks / Notes</label>
                <input style={inputStyle} placeholder="Optional remarks" value={remarks} onChange={e => setRemarks(e.target.value)} />
            </div>

            {/* SUBMIT BUTTON */}
            <button style={mainSaveBtn} onClick={submitForm} disabled={loading}>
                {loading ? 'Processing...' : 'Save Purchase Transaction'}
            </button>

            {showCustomerModal && (
                <AddCustomerModal onClose={() => setShowCustomerModal(false)} onRefresh={() => { }} />
            )}
        </div>
    );
};

const CustomerDropdownSearch = ({ query, onSelect }) => {
    const [results, setResults] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(-1);

    useEffect(() => {
        let cancelled = false;
        const timer = setTimeout(() => {
            API.get(`/v1/customers/search?q=${encodeURIComponent(query)}`)
                .then(res => { 
                    if (!cancelled) {
                        setResults(res.data.data || res.data || []); 
                        setSelectedIndex(-1);
                    }
                })
                .catch(() => { if (!cancelled) setResults([]); });
        }, 300);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [query]);

    return (
        <div 
            style={dropdownStyle}
            onKeyDown={(e) => {
                if (results.length === 0) return;
                if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setSelectedIndex(prev => (prev < results.length - 1 ? prev + 1 : 0));
                } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setSelectedIndex(prev => (prev > 0 ? prev - 1 : results.length - 1));
                } else if (e.key === 'Enter' && selectedIndex >= 0) {
                    e.preventDefault();
                    onSelect(results[selectedIndex]);
                }
            }}
        >
            {results.map((c, idx) => (
                <div 
                    key={c.id} 
                    style={{ ...itemStyle, background: idx === selectedIndex ? '#f1f5f9' : '#fff' }} 
                    onClick={() => onSelect(c)}
                >
                    {c.full_name} — {c.phone} {c.cnic ? `(${c.cnic})` : ''}
                </div>
            ))}
        </div>
    );
};

// --- STYLES ---
const containerStyle = { padding: '20px', background: '#f1f5f9', minHeight: '100vh', fontFamily: "'Segoe UI', Tahoma, sans-serif", maxWidth: '1000px', margin: '0 auto' };
const headerCard = { display: 'flex', justifyContent: 'space-linejoin', alignItems: 'center', marginBottom: '15px', background: '#ffffff', padding: '12px 20px', borderRadius: '8px', border: '1px solid #e2e8f0' };
const tabContainer = { display: 'flex', gap: '6px', background: '#f8fafc', padding: '4px', borderRadius: '6px', border: '1px solid #e2e8f0' };
const tabStyle = { padding: '6px 14px', border: 'none', background: 'transparent', cursor: 'pointer', borderRadius: '4px', fontSize: '12px', fontWeight: '600', color: '#64748b' };
const activeTab = { ...tabStyle, background: '#0f172a', color: '#fff' };
const metaGrid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px', background: '#ffffff', padding: '12px 15px', borderRadius: '8px', border: '1px solid #e2e8f0' };
const metaFieldBox = { display: 'flex', alignItems: 'center', gap: '10px' };
const metaLabel = { fontWeight: '600', fontSize: '12px', color: '#475569', width: '60px' };
const sectionBox = { background: '#ffffff', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '15px' };
const labelStyle = { fontWeight: '600', fontSize: '12px', color: '#334155' };
const miniLabel = { fontSize: '11px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', marginBottom: '4px', display: 'block' };
const inputStyle = { width: '100%', padding: '7px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px', outline: 'none', boxSizing: 'border-box', background: '#fff' };
const disabledInput = { ...inputStyle, background: '#f8fafc', color: '#64748b' };
const errorInput = { ...inputStyle, border: '1px solid #ef4444', background: '#fef2f2' };
const paymentRow = { display: 'grid', gridTemplateColumns: '1fr 2fr 1fr auto', gap: '10px', marginTop: '8px', background: '#f8fafc', padding: '8px', borderRadius: '6px', alignItems: 'center', border: '1px solid #e2e8f0' };
const addBtnStyle = { padding: '5px 12px', background: '#f1f5f9', color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' };
const deleteBtn = { background: '#fee2e2', color: '#ef4444', border: 'none', width: '28px', height: '28px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' };
const mainSaveBtn = { width: '100%', padding: '12px', background: '#0f172a', color: '#fff', borderRadius: '8px', fontWeight: '700', fontSize: '14px', cursor: 'pointer', border: 'none', marginTop: '5px' };
const confirmBox = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f0fdf4', padding: '6px 10px', borderRadius: '6px', border: '1px solid #dcfce7', fontSize: '12px' };
const changeBtn = { background: '#fff', color: '#dc2626', border: '1px solid #fecaca', padding: '3px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' };
const dropdownStyle = { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', zIndex: 100, border: '1px solid #cbd5e1', borderRadius: '6px', maxHeight: '200px', overflowY: 'auto' };
const itemStyle = { padding: '8px 12px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid #f1f5f9' };

export default AddPurchaseForm;